import type {
    DelsumInput,
    KostnadskjemaSummary,
    KostnadskjemaUserInput,
    KostnadslinjeInput,
    Kostnadstype,
    Tilleggssatser,
} from '../../../services/types';
import {
    HOVEDPROSJEKT_DELSUM_NAVN,
    reconcileMvaDelsum,
} from '../../Lonnsomhet/mvaDelsum';

// Prosjektleder-timer er ikke lenger en kostnadslinje - den dekkes av `prosjektlederSats`.
// Skjemaer som allerede har en Prosjektleder-linje beholder den; den er brukerdata.
export const DEFAULT_KOSTNADSTYPER_LABELS = ['Kvadratmeterpris'];

const GetDefaultKostnadslinjer = (kostnadstyper: Array<Kostnadstype>) => {
    const result: Array<KostnadslinjeInput> = [];

    for (const label of DEFAULT_KOSTNADSTYPER_LABELS) {
        const type = kostnadstyper.find((k) => k.navn === label);
        if (type) {
            result.push({
                kostnadstype: type,
                antallEnhet: 0,
                prisPrEnhet: type.defaultCost ?? 0,
                andelKostnadsforing: type.defaultKostnadsforingAndel ?? 0,
                andelAktivering: 0,
            });
        }
    }

    return result;
};

/**
 * Maps KostnadskjemaSummary (with calculated fields) to KostnadskjemaUserInput (user input only).
 * Removes all calculated fields to prevent them from being managed by Formik.
 *
 * `tilleggssatser` holds the backend's default percentages; they seed a brand new schema.
 * An existing schema keeps whatever was saved, falling back to 0 for rows written before
 * the satser existed - those must not change total cost retroactively.
 *
 * `mvapliktig` is a process variable set on the schema before the kostnadskalkyle is
 * filled in; like the areal fields it is consumed here so the initial `delsummer` are
 * reconciled with it (MVA row present iff 0-99 %). Pass null to skip reconciliation
 * (unknown value or read-only/completed schema).
 */
export const getInitialValues = (
    kostnadskjema: KostnadskjemaSummary | undefined,
    prosjektId: string,
    prosessInstansId: string,
    kostnadstyper: Array<Kostnadstype>,
    prosjektarealFromSchema: string | null,
    leieobjektArealFromSchema: string | null,
    mvapliktig: number | null,
    tilleggssatser: Tilleggssatser | undefined,
): KostnadskjemaUserInput => {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const prosjektareal = parseFloat(prosjektarealFromSchema || '0');
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const leieobjectAreal = parseFloat(leieobjektArealFromSchema || '0');

    // Initial values when no existing kostnadskjema. Hovedprosjekt is always prefilled
    // at index 0 (mirrors the backend's read-path bootstrap) so the very first save
    // persists it — and so the MVA row can be reconciled in from the start. Its kostnad
    // is backend-calculated on every read, so it is not set here.
    if (!kostnadskjema || Object.keys(kostnadskjema).length === 0) {
        const delsummer: Array<DelsumInput> = [
            {
                navn: HOVEDPROSJEKT_DELSUM_NAVN,
                avskrivningstid: 0,
                restverdiIProsent: 0,
            },
        ];

        return {
            id: 0,
            prosjektId,
            prosessInstansId,
            kostnadslinjer: GetDefaultKostnadslinjer(kostnadstyper),
            risikotilleggSats: tilleggssatser?.risikotilleggSats ?? 0,
            prosjektlederSats: tilleggssatser?.prosjektlederSats ?? 0,
            avkastningskrav: 0,
            inflasjon: 0,
            terminerPerAar: 4,
            grunnleiePerM2: 0,
            prosjektArealM2: prosjektareal,
            leieobjektArealM2: leieobjectAreal,
            kontraktsleiePerM2: 0,
            leieperiode: 0,
            delsummer: reconcileMvaDelsum(delsummer, mvapliktig) ?? delsummer,
        };
    }

    // Map kostnadslinjer to input-only format
    const kostnadslinjer: Array<KostnadslinjeInput> =
        kostnadskjema.kostnadslinjer.map((kl) => ({
            id: kl.id,
            kostnadstype: kl.kostnadstype,
            antallEnhet: kl.antallEnhet,
            prisPrEnhet: kl.prisPrEnhet,
            andelAktivering: kl.andelAktivering ?? 0,
            andelKostnadsforing: kl.andelKostnadsforing,
        }));

    // Map delsummer - include kostnad for initialization (Hovedprosjekt and MVA rows are
    // auto-calculated, remaining rows are user input)
    const delsummer: Array<DelsumInput> = kostnadskjema.delsummer.map((ds) => ({
        id: ds.id,
        navn: ds.navn,
        avskrivningstid: ds.avskrivningstid,
        restverdiIProsent: ds.restverdiIProsent ?? 0,
        kostnad: ds.kostnad ?? 0,
    }));

    return {
        id: kostnadskjema.id,
        prosjektId: kostnadskjema.prosjektId,
        prosessInstansId: kostnadskjema.prosessInstansId,
        kostnadslinjer,
        // Fallback til 0 for skjemaer lagret før satsene fantes, slik at totalkostnaden
        // deres ikke endrer seg med tilbakevirkende kraft.
        risikotilleggSats: kostnadskjema.risikotilleggSats ?? 0,
        prosjektlederSats: kostnadskjema.prosjektlederSats ?? 0,
        prosjektArealM2: prosjektareal,
        leieobjektArealM2: leieobjectAreal,
        kontraktsleiePerM2: kostnadskjema.kontraktsleiePerM2,
        leieperiode: kostnadskjema.leieperiode ?? 0, // Fallback to 0 due to older entries without this field
        avkastningskrav: kostnadskjema.avkastningskrav,
        inflasjon: kostnadskjema.inflasjon,
        terminerPerAar: kostnadskjema.terminerPerAar || 4,
        grunnleiePerM2: kostnadskjema.grunnleiePerM2,
        // Align the MVA row with mvapliktig (add at 0-99 %, remove at 100 %). On a list
        // without a Hovedprosjekt row this is a no-op — the backend safety net owns that case.
        delsummer: reconcileMvaDelsum(delsummer, mvapliktig) ?? delsummer,
    };
};
